import {
  AloftTime,
  addressToMultilineAddress,
  formatAppraisalType,
  formatShortenedPropertyType,
} from '@aloft/toolkit-shared';
import { Block } from '@aloft/toolkit-sui';

import { Link } from 'react-router-dom';

import { ListCard } from '../../../../components/ListCard.tsx';
import { TkOrderListing } from '../OrderTable/types.ts';
import { OrderActionsMenu } from './OrderActionsMenu.tsx';
import { OrderCardDatum } from './OrderCardDatum.tsx';
import { OrderCardTags } from './OrderCardTags.tsx';
import { getBadgeFromOrder } from './utils/getBadgeFromOrder.ts';

export const Tkv2OrderCard = ({
  order,
  to,
  updateOrder,
  deleteOrder,
  downloadReport,
  openDocumentsPage,
}: {
  order: TkOrderListing;
  to: string;
  updateOrder: () => void;
  deleteOrder: () => void;
  downloadReport: () => void;
  openDocumentsPage: () => void;
}) => {
  const clientTimezone = AloftTime.getCurrentTimeZone();
  const mlAddress = addressToMultilineAddress(order.address ?? {});
  const createdAt = order?.createdAt;
  const effectiveDate = order?.effectiveDate;
  let primaryDate = '';
  if (order.isMock) {
    primaryDate = 'None';
  } else if (order.isCompleted) {
    primaryDate = `Signed ${order.signedAt?.toAloftDate().format('MM/dd/yyyy') ?? ''}`;
  } else if (order.inspectifyOrder) {
    primaryDate = `Due ${order.dueDate?.format('MM/dd/yyyy') ?? ''}`;
  } else {
    primaryDate = `Effective ${effectiveDate?.format('MM/dd/yyyy') ?? ''}`;
  }
  const secondaryDate = order.inspectifyOrder
    ? `Assigned ${createdAt?.formatTime(clientTimezone, 'MM/dd/yyyy') ?? ''}`
    : `Created ${createdAt?.formatTime(clientTimezone, 'MM/dd/yyyy') ?? ''}`;

  const WrapperComponent = order.isCompleted
    ? Block
    : (props: React.PropsWithChildren) => (
      <Link
        to={!order.isCraftsman ? to.replace('paired-cost/analysis', 'legacy-export') : to}
        {...props}
      />
    );

  return (
    <WrapperComponent>
      <ListCard
        className="orders-list-card"
        role="group"
        style={{ cursor: order.isCompleted ? 'default' : 'pointer' }}
      >
        <OrderCardDatum
          label="Address"
          textPrimary={mlAddress[0]}
          textSecondary={mlAddress[1]}
          badge={getBadgeFromOrder(order)}
        />
        <OrderCardDatum
          label="Type"
          textPrimary={formatShortenedPropertyType(order.propertyType) ?? ''}
        />
        <OrderCardDatum
          label="Appraisal Type"
          textPrimary={formatAppraisalType(order.appraisalType) ?? ''}
          textSecondary={order.orderNumber ? `ID ${order.orderNumber}` : ''}
        />
        <OrderCardDatum
          label="Client"
          textPrimary={order.clientName ?? ''}
          textSecondary={order.appraiserFee !== undefined ? order.appraiserFee.toString(true) : ''}
        />
        <OrderCardDatum label="Effective" textPrimary={primaryDate} textSecondary={secondaryDate} />
        <OrderCardTags tags={order?.tags || []} orderID={order.id} />
        <OrderActionsMenu
          orderID={order.id}
          address={mlAddress[0]}
          isArchived={order.isArchived}
          isTraining={order.isTraining}
          isCompleted={order.isCompleted}
          updateOrder={updateOrder}
          deleteOrder={deleteOrder}
          downloadReport={downloadReport}
          openDocumentsPage={openDocumentsPage}
          isInspectifyOrder={!!order.inspectifyOrder}
          isMock={!!order.isMock}
        />
      </ListCard>
    </WrapperComponent>
  );
};
